import {
  DossDataFormat,
  FormCategories,
  FormData,
  FormFieldDefaultValueData,
  FormFields,
  FormPages,
  isArrayCellData,
  isFileCellData,
  isLookupCellData,
  isResolvedArrayCellData,
  isFormulaType,
  isValidArrayInnerType,
  isValidDateString,
  isValidDateTimeString,
  isValidDefaultValue,
  isValidFormFieldMetadata,
  shortTextFormFieldMetadataSchema,
  signatureFormFieldMetadataSchema,
  utcMidnightToISOString,
} from '@doss/common-all'
import dayjs from 'dayjs'
import { isEqual } from 'lodash'
import { DetailedForm, DetailedFormCategory } from '~/components/forms/types'
import { CellValue } from '~/components/records/add-record/AddRecordDialog'
import { AddRecordTableColumn, RecordDetailTableCell } from '~/components/records/types'
import { DossTableCell } from '../../components/tables/types'
import { DataType, FormFieldType } from '../generated/apollo-operations'
import { getValueFromRow, RecordDetailTableRowWithoutButtons } from './table'
import { hasCellDataChanged } from './table-cell'

/**
 * For empty string formik values, returns null
 */
const normalize = (value: unknown) => (value === null || value === '' ? null : value)

/**
 * We can't use formik's dirty state because it does not work when formik.setValues is used. This is
 * because formik.setValues changes formik.initialValues as well for some reason which results in
 * formik.dirty to be false even if values have changed. Therefore, we do our own lodash comparison
 * whenever we need to check the formik dirty state.
 */
export function checkFormikDirtyState<T extends object>(initialValues: T, values: T): boolean {
  const normalizedInitialSubset = Object.fromEntries(
    Object.entries(initialValues).map(([key, value]) => [key, normalize(value)]),
  )

  const normalizedValuesSubset = Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, normalize(value)]),
  )

  const modifiedInitial = { ...normalizedInitialSubset }
  const modifiedValues = { ...normalizedValuesSubset }

  // For date values, check if month, day and year are the same
  const dateKeys = Object.keys(modifiedInitial).filter((key) => {
    const initialValue = modifiedInitial[key]
    const currentValue = modifiedValues[key]

    return (
      isValidDateString(initialValue?.toString() ?? '') ||
      isValidDateString(currentValue?.toString() ?? '')
    )
  })

  dateKeys.forEach((key) => {
    const initialValue = modifiedInitial[key]
    const currentValue = modifiedValues[key]

    if (
      initialValue &&
      currentValue &&
      typeof initialValue === 'string' &&
      typeof currentValue === 'string'
    ) {
      try {
        const initialDate = dayjs.utc(initialValue)
        const currentDate = dayjs.utc(currentValue)

        // Compare only month, day, and year
        if (
          initialDate.year() === currentDate.year() &&
          initialDate.month() === currentDate.month() &&
          initialDate.date() === currentDate.date()
        ) {
          // Set both to the same normalized date value
          const normalizedDate = initialDate.startOf('day').format()
          modifiedInitial[key] = normalizedDate
          modifiedValues[key] = normalizedDate
        }
      } catch (e) {
        console.error('Error normalizing date value:', e)
      }
    }
  })

  // For datetime values, check if hour and minute are the same
  const dateTimeKeys: string[] = Object.keys(modifiedInitial).filter((key) => {
    const initialValue = modifiedInitial[key]
    const currentValue = modifiedValues[key]

    if (
      initialValue &&
      currentValue &&
      typeof initialValue === 'string' &&
      typeof currentValue === 'string'
    ) {
      return isValidDateTimeString(initialValue) || isValidDateTimeString(currentValue)
    }

    return false
  })

  dateTimeKeys.forEach((key) => {
    const initialValue = modifiedInitial[key]
    const currentValue = modifiedValues[key]
    if (
      initialValue &&
      currentValue &&
      typeof initialValue === 'string' &&
      typeof currentValue === 'string'
    ) {
      try {
        const initialDate = dayjs.utc(initialValue).startOf('minute')
        const currentDate = dayjs.utc(currentValue).startOf('minute')

        if (initialDate.isSame(currentDate)) {
          // Set both to the same normalized datetime value (minute precision)
          modifiedInitial[key] = initialDate.format()
          modifiedValues[key] = currentDate.format()
        }
      } catch (e) {
        console.error('Error normalizing datetime value:', e)
      }
    }
  })

  return !isEqual(modifiedInitial, modifiedValues)
}

/**
 * Counts the number of values that are different between initialValues and values.
 */
export function countDifferences<T extends Record<string, CellValue>>(
  initialValues: T,
  values: T,
  cells: RecordDetailTableCell[],
): number {
  const allKeys = new Set([...Object.keys(initialValues), ...Object.keys(values)]) as Set<keyof T>

  return Array.from(allKeys).reduce((diffCount, key) => {
    const cell = cells.find((cell) => cell.column.id === key)
    if (!cell) {
      return diffCount // If no cell is found, don't count it as a difference
    }

    const hasChanged = hasCellDataChanged(
      cell.column,
      cell.data,
      values[key],
      cell.lookupCells.map((lookupCell) => lookupCell.id),
      cell.documents.map((document) => document.id),
      cell.users.map((user) => user.id),
    )

    return hasChanged ? diffCount + 1 : diffCount
  }, 0)
}

/**
 * Function to get initial values for a form
 */
export function getFormInitialValues(form: DetailedForm, isMobile: boolean): FormData {
  // Create the initial pages object
  const pages: FormPages = {}

  form.pages.forEach((page) => {
    // Create the initial categories object
    const categories: FormCategories = {}

    page.categories.forEach((category) => {
      let fields = [getFormFieldsInitialInput(category)]
      if (category.isArray && isMobile) {
        fields = []
      }
      categories[category.id] = {
        name: category.name,
        order: category.order,
        fields,
      }
    })

    pages[page.id] = {
      name: page.name,
      order: page.order,
      categories,
    }
  })

  return {
    formId: form.id,
    name: form.name,
    pages,
  }
}

/**
 * Converts the default value into a formatted string
 */
function formatDefaultValue(data: FormFieldDefaultValueData): string {
  if (Array.isArray(data)) {
    // Join array elements into a single string with commas
    return data.join(', ')
  }

  // For primitive types (string, number, boolean), convert directly to string
  return String(data)
}

/**
 * Function to get initial values for the form fields in a form category
 */
export function getFormFieldsInitialInput(category: DetailedFormCategory): FormFields {
  const fields: FormFields = {}

  category.fields.forEach((field) => {
    const fieldType = field.type
    const defaultValue = field.defaultValue
    let defaultValueData = defaultValue ? isValidDefaultValue(field.type, defaultValue).data : null
    const validatedMetadata = isValidFormFieldMetadata(field.metadata, field.type)
      ? field.metadata
      : null

    // For DATE and DATE_TIME types, if the default value is now, return the current date
    if (fieldType === FormFieldType.DATE && defaultValueData === 'now') {
      // dayjs will run on browser timezone and return the correct date for "now" in the user's local time in ISO format
      defaultValueData = utcMidnightToISOString()
    } else if (fieldType === FormFieldType.DATE_TIME && defaultValueData === 'now') {
      defaultValueData = dayjs().toISOString()
    } else if (fieldType === FormFieldType.FILE) {
      defaultValueData = []
    }

    const formattedDefaultValueData = defaultValueData ? formatDefaultValue(defaultValueData) : null

    fields[field.id] = {
      name: field.name,
      order: field.order,
      value: defaultValueData,
      type: field.type,
      lookupColumnId: field.lookupColumn?.id,
      formattedValue: formattedDefaultValueData,
      isArray: field.isArray ?? false,
      shouldLinkUserToSignature: signatureFormFieldMetadataSchema.isValidSync(validatedMetadata)
        ? validatedMetadata?.linkUserToSignature
        : undefined,
      isMobileBarcode: shortTextFormFieldMetadataSchema.isValidSync(validatedMetadata)
        ? validatedMetadata?.isMobileBarcode
        : undefined,
      isScannerBarcode: shortTextFormFieldMetadataSchema.isValidSync(validatedMetadata)
        ? validatedMetadata?.isScannerBarcode
        : undefined,
      isOverwritingLookup: false,
      lookupCellId: null,
      lookupCellIds: null,
      lookupRowId: null,
      lookupRowIds: null,
    }
  })

  return fields
}

// Function to get the initial values of a record from a table row
export function getRecordInitialValues(row: RecordDetailTableRowWithoutButtons) {
  return row.cells.reduce<Record<string, CellValue>>((acc, cell) => {
    if (cell.column.type === DataType.LOOKUP) {
      // Single lookup cell case. Preserve the lookup selection even when the selected target
      // cell's resolved display value is null.
      if (cell.lookupCells.length === 1) {
        const lookupData = isLookupCellData(cell.data) ? cell.data.data : null
        const userVisibleValue =
          lookupData !== null && lookupData !== undefined
            ? lookupData.toString()
            : (cell.userVisibleValue ?? '')
        acc[cell.column.id] = [
          {
            ...cell.lookupCells[0],
            userVisibleValue,
            data: { type: DossDataFormat.LOOKUP, data: lookupData },
          },
        ]
        return acc
      }

      if (!isLookupCellData(cell.data) || cell.lookupCells.length > 0) {
        acc[cell.column.id] = []
        return acc
      }

      // Empty lookup cell case with overwritten data from Elasticsearch.
      if (cell.data.isOverwritten) {
        if (typeof cell.data.data === 'number' || typeof cell.data.data === 'string') {
          acc[cell.column.id] = {
            type: DossDataFormat.LOOKUP,
            data: cell.data.data,
            isOverwritten: true,
          }
          return acc
        }

        // Empty lookup cell case with overwritten data from Postgres.
        const lookupType = cell.column.lookupColumn?.type
        const isValidString = lookupType === DataType.STRING
        const parsedNumber = Number(cell.userVisibleValue)
        const isValidNumber = lookupType === DataType.NUMBER && !isNaN(parsedNumber)

        if (isValidString || isValidNumber) {
          acc[cell.column.id] = {
            type: DossDataFormat.LOOKUP,
            data: lookupType === DataType.NUMBER ? parsedNumber : cell.userVisibleValue,
            isOverwritten: true,
          }
          return acc
        }
      } else if (cell.data.data) {
        // Handle cases where user does not have access to the lookup cell.
        acc[cell.column.id] = {
          type: DossDataFormat.LOOKUP,
          data: cell.data.data,
          isOverwritten: false,
        }
        return acc
      }

      acc[cell.column.id] = []
      return acc
    }

    if (
      cell.column.type === DataType.ARRAY &&
      cell.column.innerType === DataType.LOOKUP &&
      isResolvedArrayCellData(cell.data)
    ) {
      const values = cell.data.data.map((value) => (value === null ? '' : value.toString()))

      acc[cell.column.id] = cell.lookupCells.map((lookupCell, index) => ({
        ...lookupCell,
        userVisibleValue: values[index],
        data: { type: DossDataFormat.ARRAY, data: cell.data?.data },
      }))

      return acc
    }

    if (cell.documents.length > 0) {
      if (isFileCellData(cell.data)) {
        acc[cell.column.id] = cell.documents[0].id
      } else if (isArrayCellData(cell.data)) {
        acc[cell.column.id] = cell.documents.map((document) => document.id)
      }
      return acc
    }

    if (cell.users.length > 0) {
      if (cell.column.type === DataType.USER) {
        acc[cell.column.id] = [cell.users[0]]
      } else if (cell.column.type === DataType.ARRAY && cell.column.innerType === DataType.USER) {
        acc[cell.column.id] = cell.users
      }
      return acc
    }

    // Make sure that BOOLEAN type values are boolean
    if (cell.column.type === DataType.BOOLEAN) {
      const value = getValueFromRow(row, cell.column, false)
      if (value && typeof value === 'string') {
        const booleanValue = value === 'true'
        acc[cell.column.id] = booleanValue
        return acc
      }
      acc[cell.column.id] = value
      return acc
    }

    acc[cell.column.id] = getValueFromRow(row, cell.column, false)
    return acc
  }, {})
}

// Function to get the initial values of a new record from the columns
export function getAddRecordInitialValues(columns: AddRecordTableColumn[]) {
  return columns.reduce<Record<string, CellValue>>((acc, column) => {
    if (isFormulaType(column.type)) {
      return acc
    }

    if (column.type === DataType.ARRAY) {
      acc[column.id] = []
      return acc
    }

    if (column.type === DataType.BOOLEAN) {
      acc[column.id] = false
      return acc
    }

    acc[column.id] = null

    return acc
  }, {})
}

// Function to get the initial values of a table cell
export function getTableCellInitialValues(
  cell: DossTableCell,
  value: string[] | string | boolean | number | null,
): Record<string, CellValue> {
  switch (cell.column.type) {
    case DataType.BOOLEAN:
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.ENUM:
    case DataType.NUMBER:
    case DataType.STRING:
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
    case DataType.ID:
      return { [cell.column.id]: value }
    case DataType.FORMULA_BOOLEAN:
      // FORMULA_BOOLEAN is out of scope for the non-numeric formulas project
      return {}
    case DataType.LOOKUP: {
      if (cell.lookupCells.length > 0) {
        return {
          [cell.column.id]: cell.lookupCells.map((lookupCell) => ({
            ...lookupCell,
            userVisibleValue: cell.userVisibleValue || '',
            data: { type: DossDataFormat.ARRAY, data: cell.data?.data },
          })),
        }
      }
      // Handle when overwriting the lookup with a user entered value
      if (isLookupCellData(cell.data) && cell.data.data) {
        return { [cell.column.id]: cell.data }
      }
      return { [cell.column.id]: [] }
    }

    case DataType.FILE_URL:
      return {
        [cell.column.id]: cell.documents.length > 0 ? cell.documents[0].id : null,
      }
    case DataType.USER:
      return { [cell.column.id]: cell.users.length > 0 ? cell.users : value }
    case DataType.ARRAY: {
      if (!isValidArrayInnerType(cell.column.innerType)) {
        return { [cell.column.id]: value }
      }
      switch (cell.column.innerType) {
        case DataType.ENUM:
          return { [cell.column.id]: value }
        case DataType.LOOKUP:
          return {
            [cell.column.id]: cell.lookupCells.map((lookupCell) => ({
              ...lookupCell,
              userVisibleValue: cell.userVisibleValue || '',
              data: { type: DossDataFormat.LOOKUP, data: cell.data?.data },
            })),
          }
        case DataType.FILE_URL:
          return {
            [cell.column.id]: cell.documents.map((document) => document.id),
          }
        case DataType.USER:
          return { [cell.column.id]: cell.users }
      }
    }
  }
}
